// #25 RecoTiles and #26 HireBands. One radiogroup of equal tiles in DC-37's three forms: "reserved" (Hire · No Hire
// with the third column kept empty until Q-E13), 6 (the debrief outcomes, 2 × 3) and 4 (the next paths: Step 8's
// Advance · Hold · Pass · Withdrawn, or the decline paths). A chosen tile that has fields discloses them in a panel
// under the grid, named by that tile. A blocked tile (VP+ rule applied) is dashed, keeps its tab stop and is described
// by its reason; the refused form is the red banner on top with the choice kept.
// HireBands is the legend, drawn without the middle band (Q-E13).
// form="playbook" (DC-245, B14 C6): his playbook's page 7, one full-width tinted row per offered recommendation in the
// order given (the requisition's level.offered), each row's tone from C5 and its radio at the right; a blocked row
// (the level rule binds) is dashed, keeps its tab stop and is described by the rule sentence. A slot under each row
// (children) holds what that option asks, e.g. Suggested role under Consider Other Role.

import { Fragment, type ReactNode } from "react";
import { Tooltip } from "../lib/Tooltip";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useSvgId } from "../lib/useSvgId";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { RECOMMENDATION_LABELS, type RecoTone } from "../labels";
import { Banner } from "../core/Banner";
import type { Blocked } from "../core/Button";
import { Pill } from "../core/Pill";
import { useChoice, type ChoiceItem } from "./choice";
import { HIRE_BAND_LABELS, SCORECARD_LABELS } from "./labels";

export interface RecoOption {
  value: string;
  /** His word for the tile. */
  label: string;
  /** The tile's second line (a band, a sentence of his). */
  sub?: ReactNode | undefined;
  /** Disclosed under the grid while this tile is chosen. */
  fields?: ReactNode | undefined;
  blocked?: Blocked | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
}

export type RecoColumns = "reserved" | 6 | 4;

export interface RecoTilesProps {
  /** The tiles (default). */
  form?: "tiles" | undefined;
  options: readonly RecoOption[];
  /** "reserved": three columns with the third kept empty (Q-E13); 6: two rows of three; 4: one row of four. */
  columns?: RecoColumns | undefined;
  /** The group's name. Default "Recommendation". */
  label?: string | undefined;
  value?: string | null | undefined;
  defaultValue?: string | null | undefined;
  onChange?: ((value: string) => void) | undefined;
  can?: boolean | undefined;
  /** The whole group blocked (every tile dashed, one reason). */
  blocked?: Blocked | undefined;
  /** The server said no: his rule sentence on the red banner above the tiles; the choice is kept. */
  refused?: { reason: string } | undefined;
  mode?: "edit" | "view" | undefined;
  className?: string | undefined;
}

/** Hire · No Hire, with the Hire band as Hire's second line. */
export const RECOMMENDATION_OPTIONS: readonly RecoOption[] = [
  { value: "hire", label: RECOMMENDATION_LABELS.hire, sub: HIRE_BAND_LABELS.hireRange },
  { value: "noHire", label: RECOMMENDATION_LABELS.noHire },
];

function Tile({ option, radio, checked, blocked, describedBy, labelId }: {
  option: RecoOption; radio: ChoiceItem; checked: boolean; blocked: boolean; describedBy: string | undefined; labelId: string;
}) {
  const hot = useHs(option.hs);
  return (
    <div
      {...radio}
      className={cx("mc-rtile", checked && "mc-on", blocked && "mc-dis")}
      aria-labelledby={labelId}
      aria-disabled={blocked ? true : undefined}
      aria-describedby={describedBy}
      {...hot}
    >
      <span className="mc-rd" aria-hidden="true" />
      <span className="mc-rx">
        <b id={labelId}>{option.label}</b>
        {option.sub !== undefined && option.sub !== null ? <span>{option.sub}</span> : null}
      </span>
    </div>
  );
}

/** One playbook row: a stored recommendation value, its word, its one-line description and its tone (C5). */
export interface PlaybookRecoOption {
  value: string;
  label: string;
  description?: string | undefined;
  tone: RecoTone;
  blocked?: { reason: string } | undefined;
}

export interface RecoTilesPlaybookProps {
  form: "playbook";
  options: readonly PlaybookRecoOption[];
  value: string | null;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  onChange(value: string): void;
  /** The slot under an option (e.g. Suggested role under Consider Other Role); null draws nothing. */
  children?: ((value: string) => ReactNode) | undefined;
  /** The group's name. Default "Recommendation". */
  label?: string | undefined;
  /** The group's id (the Gate's row focuses it). */
  id?: string | undefined;
  className?: string | undefined;
}

export type AnyRecoTilesProps = RecoTilesProps | RecoTilesPlaybookProps;

/** Keys typed inside an option's slot (a text field) stay in the field: the group's arrows never see them. */
const keepKeys = (event: { stopPropagation(): void }) => event.stopPropagation();

function PlaybookRows({ options, value, disabled, invalid, onChange, children, label = SCORECARD_LABELS.recommendation, id, className }: RecoTilesPlaybookProps) {
  const uid = useSvgId();
  const values = options.map((option) => option.value);
  const isBlocked = (v: string) => Boolean(disabled) || options.find((option) => option.value === v)?.blocked !== undefined;
  const choice = useChoice({ values, value, onChange, isBlocked });
  return (
    <div className={cx("mc-preco-w", className)}>
      <div
        role="radiogroup"
        id={id}
        aria-label={label}
        aria-invalid={invalid ? true : undefined}
        aria-disabled={disabled ? true : undefined}
        className={cx("mc-preco", invalid && "mc-preco-err")}
        {...choice.container}
      >
        {options.map((option, at) => {
          const labelId = `${uid}-p-${at}`;
          const whyId = option.blocked ? `${uid}-pwhy-${at}` : undefined;
          const checked = at === choice.index;
          const row = (
            <div
              {...choice.item(at)}
              className={cx("mc-prow", `mc-prow-${option.tone}`, checked && "mc-on", (option.blocked || disabled) && "mc-dis")}
              aria-labelledby={labelId}
              aria-disabled={option.blocked || disabled ? true : undefined}
              aria-describedby={whyId}
            >
              <span className="mc-prow-x">
                <b id={labelId}>{option.label}</b>
                {option.description ? <span>{option.description}</span> : null}
              </span>
              <span className="mc-rd" aria-hidden="true" />
            </div>
          );
          const slot = children?.(option.value);
          return (
            <div key={option.value} className="mc-prow-w">
              {option.blocked ? <Tooltip label={option.blocked.reason} mode="mirror">{row}</Tooltip> : row}
              {option.blocked ? <VisuallyHidden id={whyId}>{option.blocked.reason}</VisuallyHidden> : null}
              {slot !== undefined && slot !== null && slot !== false ? (
                <div className="mc-prow-slot" role="group" aria-labelledby={labelId} onKeyDown={keepKeys}>{slot}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RecoTiles(props: AnyRecoTilesProps) {
  if (props.form === "playbook") return <PlaybookRows {...props} />;
  return <Tiles {...props} />;
}

function Tiles({
  options, columns = "reserved", label = SCORECARD_LABELS.recommendation, value, defaultValue, onChange, can, blocked, refused, mode, className,
}: RecoTilesProps) {
  const id = useSvgId();
  const shown = options.filter((option) => option.can !== false);
  const values = shown.map((option) => option.value);
  const isBlocked = (v: string) => blocked !== undefined || shown.find((option) => option.value === v)?.blocked !== undefined;
  const choice = useChoice({ values, value, defaultValue, onChange, isBlocked });
  if (can === false) return null;
  const chosen = shown[choice.index];
  if (mode === "view") {
    return (
      <span className={cx("mc-reco-v", className)}>
        <VisuallyHidden>{label}</VisuallyHidden>
        {chosen ? <Pill>{chosen.label}</Pill> : "—"}
      </span>
    );
  }
  const groupWhy = `${id}-why`;
  const tiles = shown.map((option, at) => {
    const own = option.blocked ?? blocked;
    const why = own ? (option.blocked ? `${id}-why-${at}` : groupWhy) : undefined;
    const tile = (
      <Tile
        option={option}
        radio={choice.item(at)}
        checked={at === choice.index}
        blocked={own !== undefined}
        describedBy={why}
        labelId={`${id}-t-${at}`}
      />
    );
    return (
      <Fragment key={option.value}>
        {option.blocked ? <Tooltip label={option.blocked.reason} mode="mirror">{tile}</Tooltip> : tile}
        {option.blocked ? <VisuallyHidden id={`${id}-why-${at}`}>{option.blocked.reason}</VisuallyHidden> : null}
      </Fragment>
    );
  });
  const panelId = `${id}-fields`;
  return (
    <div className={cx("mc-reco-w", className)}>
      {refused ? <Banner tone="red" live className="mc-reco-refused">{refused.reason}</Banner> : null}
      <div
        role="radiogroup"
        aria-label={label}
        aria-describedby={blocked ? groupWhy : undefined}
        aria-disabled={blocked ? true : undefined}
        className={cx("mc-reco", columns === 6 && "mc-reco-6", columns === 4 && "mc-reco-4")}
        {...choice.container}
      >
        {tiles}
        {columns === "reserved" ? <span className="mc-rtile-slot" aria-hidden="true" /> : null}
      </div>
      {blocked ? <VisuallyHidden id={groupWhy}>{blocked.reason}</VisuallyHidden> : null}
      {chosen?.fields ? (
        <div className="mc-reco-fields" role="group" id={panelId} aria-labelledby={`${id}-t-${choice.index}`}>
          {chosen.fields}
        </div>
      ) : null}
    </div>
  );
}

export interface HireBandsProps {
  className?: string | undefined;
}

/** The legend: the Hire band only (the middle band waits on Q-E13; there is no No Hire band in his words). */
export function HireBands({ className }: HireBandsProps) {
  return (
    <p className={cx("mc-bands", className)}>
      <span><b>{HIRE_BAND_LABELS.hireRange}</b> {RECOMMENDATION_LABELS.hire}</span>
    </p>
  );
}
